'use client';

import { FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ApiError,
  login,
  setupEmail,
  verifyTwoFactor,
} from '@torlin/api-client';
import { localePath, loginCopy, type Locale } from '@/i18n/public';

const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '';

type Step =
  | { kind: 'credentials' }
  | { kind: 'emailSetup' }
  | { kind: 'verification'; maskedEmail: string };

function errorMessage(error: unknown, fallback: string, field?: string): string {
  if (error instanceof ApiError) {
    const fieldError = field ? error.errors[field]?.[0] : undefined;
    return fieldError ?? error.message;
  }

  return fallback;
}

export function LoginForm({ locale }: { locale: Locale }) {
  const router = useRouter();
  const copy = loginCopy[locale];
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submitCredentials(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await login(apiUrl, { login: loginIdentifier, password }, locale);
      setPassword('');
      setStep(
        response.status === 'email_setup_required'
          ? { kind: 'emailSetup' }
          : {
              kind: 'verification',
              maskedEmail: response.masked_email,
            },
      );
    } catch (caught) {
      setError(errorMessage(caught, copy.connectionError, 'login'));
    } finally {
      setLoading(false);
    }
  }

  async function submitEmailSetup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await setupEmail(
        apiUrl,
        email,
        emailConfirmation,
        locale,
      );
      setStep({
        kind: 'verification',
        maskedEmail: response.masked_email,
      });
    } catch (caught) {
      setError(errorMessage(caught, copy.connectionError, 'email'));
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await verifyTwoFactor(apiUrl, code, locale);
      router.replace(localePath(locale, '/app'));
      router.refresh();
    } catch (caught) {
      setError(errorMessage(caught, copy.connectionError, 'code'));
    } finally {
      setLoading(false);
    }
  }

  if (step.kind === 'emailSetup') {
    return (
      <Box component="form" onSubmit={submitEmailSetup}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {copy.emailSetupTitle}
            </Typography>
            <Typography color="text.secondary">
              {copy.emailSetupIntro}
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            autoFocus
            fullWidth
            type="email"
            label={copy.email}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
          <TextField
            fullWidth
            type="email"
            label={copy.emailConfirmation}
            value={emailConfirmation}
            onChange={(event) => setEmailConfirmation(event.target.value)}
            autoComplete="email"
            required
          />

          <Button
            type="submit"
            size="large"
            variant="contained"
            disabled={loading || email === '' || email !== emailConfirmation}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : copy.emailSetupAction}
          </Button>

          <Button
            type="button"
            color="inherit"
            onClick={() => {
              setEmail('');
              setEmailConfirmation('');
              setError(null);
              setStep({ kind: 'credentials' });
            }}
          >
            {copy.backToLogin}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (step.kind === 'verification') {
    return (
      <Box component="form" onSubmit={submitCode}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {copy.verificationTitle}
            </Typography>
            <Typography color="text.secondary">
              {copy.verificationIntro} {step.maskedEmail}.{' '}
              {copy.verificationValidity}
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            autoFocus
            fullWidth
            label={copy.code}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
                autoComplete: 'one-time-code',
              },
            }}
            required
          />

          <Button
            type="submit"
            size="large"
            variant="contained"
            disabled={loading || code.length !== 6}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : copy.verify}
          </Button>

          <Button
            type="button"
            color="inherit"
            onClick={() => {
              setCode('');
              setError(null);
              setStep({ kind: 'credentials' });
            }}
          >
            {copy.backToLogin}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={submitCredentials}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {copy.title}
          </Typography>
          <Typography color="text.secondary">
            {copy.intro}
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          autoFocus
          fullWidth
          type="text"
          label={copy.login}
          value={loginIdentifier}
          onChange={(event) => setLoginIdentifier(event.target.value)}
          autoComplete="username"
          required
        />
        <TextField
          fullWidth
          type="password"
          label={copy.password}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />

        <Button type="submit" size="large" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : copy.continue}
        </Button>
      </Stack>
    </Box>
  );
}
